<template lang="html">
  <div>
    <detail-editor-table
      v-if="tables[0]"
      type="Table Assignment"
      icon="fti-table"
      :value="tableAssignments"
      :disabled="disabled"
      :add-action="promptNewTableAssignment"
      :hide-default-footer="hideActions"
      :hide-rows="hideAssignmentRows"
      :delete-action="deleteAction"
      :json-path="jsonPath"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :error-messages="tableErrors"
      :item-default="{
        id: null,
        reservationId: null,
        locationTableId: null,
        locationTableName: null,
        quantity: 1,
        isPlaceholder: false,
        joined: false,
        autoAssigned: null,
        notes: null
      }"
      :headers="[
        (headerEditor({label: '', field: 'autoAssigned', editor: {type: 'slot'}, thin: true})),
        (headerEditor({label: 'Table', field: 'locationTableName', editor: {type: 'slot'}, small: true})),
        (headerEditor({label: 'Used', field: 'quantityInUse', editor: {type: 'slot'}, thin: true})),
        (headerEditor({label: 'Qty.', field: 'quantity', editor: {type: 'slot'}, thin: true})),
        (headerEditor({label: 'Notes', field: 'notes', editor: {type: 'slot'}})),
      ]"
      @error="addError"
    >
      <template
        slot="autoAssigned"
        slot-scope="props"
      >
        <v-icon>{{ props.item.autoAssigned ? 'mdi-robot-outline' : 'mdi-robot-off-outline' }}</v-icon>
      </template>
      <template
        slot="locationTableName"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.locationTableName"
          readonly
          :error-messages="(props.errorMessages||[]).concat((getQuantityInUse(props.item.locationTableId) + props.item.quantity > getQuantity(props.item.locationTableId)) ?
            ['Overbooking by ' + ((getQuantityInUse(props.item.locationTableId) + props.item.quantity) - getQuantity(props.item.locationTableId))] :
            [])"
          :disabled="disabled"
        />
      </template>
      <template
        slot="quantityInUse"
        slot-scope="props"
      >
        <div style="white-space: nowrap;">
          <a
            v-if="getQuantityInUse(props.item.locationTableId)"
            target="_blank"
            :href="tableInUseSelectionUrl(props.item.locationTableId)"
          >
            {{ getQuantityInUse(props.item.locationTableId) + props.item.quantity }}
            of
            {{ getQuantity(props.item.locationTableId) }}
          </a>
          <span v-else>{{ props.item.quantity }}
            of
            {{ getQuantity(props.item.locationTableId) }}
          </span>
        </div>
      </template>
      <template
        slot="joined"
        slot-scope="props"
      >
        <v-checkbox
          :value="props.item.joined"
          :error-messages="props.errorMessages"
          :disabled="disabled"
          required
          @input="setAutoAssigned(props.item, 'joined', $event); props.errorMessages._clear(); "
        />
      </template>
      <template
        slot="quantity"
        slot-scope="props"
      >
        <v-text-field
          v-model.number="props.item.quantity"
          :rules="rules().required()"
          type="number"
          :error-messages="props.errorMessages"
          :disabled="disabled"
          required
          @blur="setAutoAssigned(props.item, 'quantity', forceInt(props.item.quantity) < 1 ? 1 : forceInt(props.item.quantity)); updatePlaceholder()"
          @change="setAutoAssigned(props.item, 'quantity', forceInt($event) < 1 ? 1 : forceInt($event)); updatePlaceholder()"
          @input="props.errorMessages._clear(); updatePlaceholder()"
        />
      </template>
      <template
        slot="notes"
        slot-scope="props"
      >
        <v-textarea
          :value="props.item.notes"
          auto-grow
          :rows="1"
          :error-messages="props.errorMessages"
          :disabled="disabled"
          @input=" setAutoAssigned(props.item, 'notes', $event); props.errorMessages._clear();"
        />
      </template>
    </detail-editor-table>

    <v-dialog
      v-model="showTableSelectDialog"
      :max-width="500"
      scrollable
    >
      <v-card>
        <v-card-title>
          <span class="headline">Tables</span>
          <v-spacer />
          <v-text-field
            v-model.trim="searchText"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
          />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-data-table
            v-model="selectedLocationTables"
            selectable-key="_allowSelect"
            :mobile-breakpoint="0"
            :headers="[
              {text: 'Table', value: 'tableName'},
              {text: 'Available', value: 'calQuantity'},
              {text: 'In Use', value: 'quantityInUse'}
            ]"
            :items="filteredLocationTables"
            hide-default-footer
            show-select
            :items-per-page="-1"
          >
            <template v-slot:item.data-table-select="{ item, isSelected, select }">
              <v-simple-checkbox
                :value="isSelected"
                :disabled="item.disabled"
                :readonly="item.disabled"
                @input="select($event)"
              />
            </template>
            <template v-slot:item.calQuantity="props">
              <span>{{ props.item.calQuantity - getQuantityInUse(props.item.tableId) }}</span>
            </template>
            <template v-slot:item.quantityInUse="props">
              <template v-if="getQuantityInUse(props.item.tableId)">
                <a
                  target="_blank"
                  :href="tableInUseSelectionUrl(props.item.tableId)"
                  @click.stop
                >
                  {{ getQuantityInUse(props.item.tableId) }} of {{ props.item.calQuantity }}
                </a>
              </template>
              <template v-else>
                <span>{{ getQuantityInUse(props.item.tableId) }} of {{ props.item.calQuantity }}</span>
              </template>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            text
            @click="showTableSelectDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="addSelectedTableAssignments"
          >
            Add Selected
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {helpers, rules, headerMethods, DetailEditorTable} from '@/ft-vue-core'

const {forceInt} = helpers

let updateDebounce

export default {
  name: 'TableAssignments',
  components: {DetailEditorTable},
  mixins: [rules, headerMethods],
  props: {
    jsonPath: String,
    getPathErrors: {
      type: [Function],
      default (path) {
        return []
      }
    },
    clearPathErrors: {
      type: [Function],
      default (path) {
      }
    },
    movePathErrors: {
      type: [Function],
      default (path) {
      }
    },
    value: Array,
    reservationId: {
      type: Number,
      default: null
    },
    tableConfig: Object,
    periodCount: Object,
    hideActions: Boolean,
    requireTableSelection: Boolean,
    disabled: Boolean,
    reloadLocationTables: Function,
    partySize: Number,
  },
  data: function () {
    return {
      searchText: '',
      hideAssignmentRows: {},
      showTableSelectDialog: false,
      selectedLocationTables: []
    }
  },
  computed: {
    openTablesMap () {
      return (this.periodCount.openTables || []).reduce((result, table) => {
        result[table.tableId] = table
        return result
      }, {})
    },
    useBy () {
      return this.periodCount.useBy || {}
    },
    tableErrors () {
      const result = []
      if (this.maxTablePartySize < 1) {
        if (this.requireTableSelection) {
          result.push('Table assignment required.')
        }
      } else if (this.partySize > this.maxTablePartySize) {
        result.push('Party size is over maximum for selected table(s)')
      } else if (this.partySize < this.minTablePartySize) {
        result.push('Party size is under minimum for selected table(s)')
      }
      return result
    },
    minTablePartySize () {
      return this.tableAssignments.reduce((a, t) => {
        const lt = this.locationTableMap[t.locationTableId]
        return lt ? this.locationTableMap[t.locationTableId].calMinCovers + a : a
      }, 0)
    },
    maxTablePartySize () {
      const joined = this.tableAssignments.reduce((a, t)=>a + t.quantity, 0) > 1
      return this.tableAssignments.reduce(
        (a, t) => {
          const lt = this.locationTableMap[t.locationTableId]
          return lt ? a + (joined ? lt.calMaxJoinCovers : lt.calMaxCovers) * t.quantity : a
        }, 0)
    },
    assignedLocationTableMap () {
      return this.value.reduce((result, assignment) => {
        const locationTableId = assignment.locationTableId
        const lt = this.locationTableMap[assignment.locationTableId]
        if (lt) {
          result[locationTableId] = lt
        }
        return result
      }, {})
    },
    filteredLocationTables () {
      return this.unassignedLocationTables.filter(lt => {
        lt._allowSelect = !lt.disabled
        return !this.searchText || lt.tableName.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
      })
    },
    unassignedLocationTables () {
      return this.tables.filter(lt => !this.assignedLocationTableMap[lt.tableId])
    },
    tables () {
      return (this.tableConfig || {}).tables || []
    },
    locationTableMap () {
      return this.tables.reduce((result, o) => {
        result[o.tableId] = o
        return result
      }, {})
    },
    tableAssignments () {
      const result = this.value.filter((ta, i) => {
        Object.keys(ta).filter(k => k[0] !== '_').forEach(k => {if (ta['__prev_' + k] === undefined) ta['__prev_' + k] = ta[k]})
        this.$set(this.hideAssignmentRows, i, !this.locationTableMap[ta.locationTableId])
        return ta.locationTableId
      })
      return result || []
    },
  },
  watch: {
    showTableSelectDialog (val, oldVal) {
      if (val && !oldVal) {
        if (this.reloadLocationTables) {
          this.reloadLocationTables()
        }
      }
    }
  },
  methods: {
    updatePlaceholder () {
      if (updateDebounce) {
        clearTimeout(updateDebounce)
      }
      updateDebounce = setTimeout(() => {
        this.$emit('update')
      }, 600)
    },
    setAutoAssigned (item, key, newVal) {
      if (newVal === '') newVal = null
      console.warn('Changing', item, key, newVal, item[key])
      const prevKey = '__prev_' + key
      const prevAutoKey = '__prev_autoAssigned'
      if (typeof item[prevAutoKey] === 'undefined') item[prevAutoKey] = item.autoAssigned
      if (typeof item[prevKey] === 'undefined') item[prevKey] = item[key]
      if (newVal === item[prevKey]) {
        item.autoAssigned = item[prevAutoKey]
      } else {
        item.autoAssigned = false
      }
      item[key] = newVal
    },
    tableInUseSelectionUrl (tableId) {
      return this.getReservationSelectListInUseByRoute(tableId).href
    },
    getReservationSelectListInUseByRoute (tableId) {
      const table = this.locationTableMap[tableId] || {}
      const use = this.useBy[tableId] || {}
      if (!table) return
      return this.$router.resolve({
        path: '/reservations/selected',
        query: {
          t: 'Using Table: ' + table.name,
          ids: Object.keys(use).join(',')
        }
      })
    },
    getOpenTable (tableId) {
      return this.openTablesMap[tableId] || {}
    },
    getQuantityInUse (tableId) {
      const table = this.getOpenTable(tableId)
      return table.quantity - table.calQuantity || 0
    },
    getQuantity (tableId) {
      return (this.locationTableMap[tableId] || {}).calQuantity
    },
    getUseBy (tableId) {
      return this.useBy[tableId] || {}
    },
    toggleAllLocationTables () {
      if (this.selectedLocationTables.length) this.selectedLocationTables = []
      else this.selectedLocationTables = this.unassignedLocationTables.slice()
    },
    forceInt,
    promptNewTableAssignment () {
      this.selectedLocationTables = []
      this.showTableSelectDialog = true
    },
    addSelectedTableAssignments () {
      for (let locationTable of this.selectedLocationTables) {
        this.addNewTableAssignment({
          id: null,
          reservationId: this.reservationId,
          locationTableId: locationTable.tableId,
          locationTableName: locationTable.tableName,
          quantity: 1,
          autoAssigned: false,
          joined: false,
          notes: null
        })
      }
      this.showTableSelectDialog = false
    },
    addNewTableAssignment (newItem) {
      this.value.push(newItem)
      this.$emit('add')
      this.updatePlaceholder()
    },
    deleteAction (rowIndex) {
      this.value.splice(rowIndex, 1)
      this.$emit('remove')
      this.updatePlaceholder()
    },
    addError (v) {
      this.$emit('error', v)
    }
  }
}
</script>
